import { useEffect, useMemo } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../Components/Button"
import { CardTotalPanel } from "../../../../Components/CardTotalPanel"
import EmptyComponent from "../../../../Components/Empty"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import RecoverCountryChart from "../RecoverCountryChart"
import Section from "../Section"

import styles from "./countriesPanel.module.less"

// "Top countries": the Alerts by Country card, ported for disputes. Same chart +
// CardTotalPanel header + "View All" into a full inner page. Top 10 by count, out of the
// same by-country/ answer the Countries page reads.
const CountriesPanel = ({ getData, range }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const byCountry = useSelector(selectSectionData(ANALYTICS_SECTION.BY_COUNTRY))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.BY_COUNTRY))

  useEffect(() => {
    getData(ANALYTICS_SECTION.BY_COUNTRY)
  }, [getData])

  const rows = useMemo(
    () =>
      (byCountry?.result ?? []).slice(0, 10).map((row) => ({
        key: row.country_code,
        count: row.count,
        value: row.amount
      })),
    [byCountry]
  )
  // Header totals must reconcile with the bars below: sum the SAME displayed rows (top 10),
  // not the whole summary, otherwise the header overshoots the visible bars.
  const totalCount = useMemo(() => rows.reduce((a, r) => a + (r.count || 0), 0), [rows])
  const totalVolume = useMemo(() => rows.reduce((a, r) => a + (r.value || 0), 0), [rows])

  const viewAll = (
    <Button
      type="link"
      onClick={() => navigate(RoutesPaths.recover.analytics_countries)}
      icon={<Icon type="chevron_right_small" />}
      iconPosition="end"
      style={{ padding: 0 }}
      size="small"
      text={t("View All")}
    />
  )

  return (
    <Section
      title={t("Top countries")}
      tooltip={t("Where your disputes originate, by cardholder country.")}
      extra={rows.length ? viewAll : undefined}
      minHeight={536}
      className={styles.fillCard}
    >
      <Spin spinning={fetching} wrapperClassName={styles.spin}>
        {rows.length ? (
          <>
            <CardTotalPanel count={totalCount} volume={totalVolume} />
            <div className={styles.chart}>
              <RecoverCountryChart rows={rows} range={range} />
            </div>
          </>
        ) : (
          <EmptyComponent type="no_data_available" />
        )}
      </Spin>
    </Section>
  )
}

export default CountriesPanel
